"use client";

import type { Day } from "@taak/react-modern-calendar-datepicker";
import { Calendar } from "@taak/react-modern-calendar-datepicker";
import "@taak/react-modern-calendar-datepicker/lib/DatePicker.css";
import React, { useEffect, useMemo, useState } from "react";
import { BlurImage } from "~/components/images/blur-image";
import { FilledButton } from "~/components/buttons/filled-button";
import { OutlineButton } from "~/components/buttons/outline-button";
import { Refresh } from "~/components/icons/refresh";
import { ChoosePlayers } from "~/components/input/choose-players";
import { Slider } from "~/components/input/slider";
import { SwipeableCalendar } from "~/components/calendar/swipeable-calendar";
import VerifyPhone from "~/components/waitlist-page/verify-phone";
import { useCourseContext } from "~/contexts/CourseContext";
import { useMe } from "~/hooks/useMe";
import { useMinHoursBeforeTeeTime } from "~/hooks/useMinHoursBeforeTeeTime";
import { api } from "~/utils/api";
import { googleAnalyticsEvent } from "~/utils/googleAnalyticsUtils";
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import timezone from "dayjs/plugin/timezone";
import { useRouter } from "next/navigation";
import { toast } from "react-toastify";

dayjs.extend(utc);
dayjs.extend(timezone);

export interface NypOfferDetails {
  courseId: string;
  date: string;
  startTime: number;
  endTime: number;
  players: number;
  pricePerPlayer: number;
}

interface NameYourPriceFormProps {
  courseId: string;
  hideWrapper?: boolean;
  initialDetails?: NypOfferDetails | null;
  onProceedToPayment: (details: NypOfferDetails) => void;
  isWaitlistMode: boolean;
  onWaitlistModeChange: (value: boolean) => void;
  // When the NameYourPrice feature flag is off, there's no choice to make — the form
  // always behaves as a plain tee-time waitlist and the mode checkbox is hidden.
  forceWaitlistOnly?: boolean;
}

export function NameYourPriceForm({
  courseId,
  hideWrapper = false,
  initialDetails,
  onProceedToPayment,
  isWaitlistMode,
  onWaitlistModeChange,
  forceWaitlistOnly = false,
}: NameYourPriceFormProps) {
  const router = useRouter();
  const { user, isLoading } = useMe();
  const { course } = useCourseContext();
  const apiUtils = api.useContext();
  const { minHours } = useMinHoursBeforeTeeTime();

  const courseStartTimeNumber = course?.courseOpenTime ?? 600;
  const courseEndTimeNumber = course?.courseCloseTime ?? 1800;
  // Default to a 2-hour window from open, not the full course day — HH+2 in this
  // military-time-as-integer format is safe since the minutes component is unchanged.
  const defaultWindowEnd = Math.min(courseStartTimeNumber + 200, courseEndTimeNumber);

  // Returning from the payment step re-mounts this form fresh — seed state from
  // initialDetails (the same values just handed to onProceedToPayment) so the buyer
  // doesn't lose what they already entered.
  const parseInitialDate = (details?: NypOfferDetails | null): Day | null => {
    if (!details) return null;
    const [year, month, day] = details.date.split("-").map(Number);
    if (!year || !month || !day) return null;
    return { year, month, day };
  };

  // NameYourPrice mode picks a single date; waitlist mode picks one or more.
  const [selectedDate, setSelectedDate] = useState<Day | null>(() => parseInitialDate(initialDetails));
  const [selectedDates, setSelectedDates] = useState<Day[]>([]);
  const [displayDate, setDisplayDate] = useState("");
  const [displayDates, setDisplayDates] = useState("");
  const [players, setPlayers] = useState(initialDetails ? String(initialDetails.players) : "1");
  const [pricePerPlayer, setPricePerPlayer] = useState(
    initialDetails ? String(initialDetails.pricePerPlayer / 100) : ""
  );
  const [fieldErrors, setFieldErrors] = useState({ date: "", price: "", phone: "" });
  const [isPhoneVerified, setIsPhoneVerified] = useState(false);

  useEffect(() => {
    if (isPhoneVerified) {
      setFieldErrors((f) => (f.phone ? { ...f, phone: "" } : f));
    }
  }, [isPhoneVerified]);

  const [localStartTime, setLocalStartTime] = useState<[number, number]>(
    initialDetails ? [initialDetails.startTime, initialDetails.endTime] : [courseStartTimeNumber, defaultWindowEnd]
  );
  const [startTime, setStartTime] = useState<[number, number]>(
    initialDetails ? [initialDetails.startTime, initialDetails.endTime] : [courseStartTimeNumber, defaultWindowEnd]
  );

  const { mutateAsync: createNotifications, isLoading: isCreatingNotifications } =
    api.userWaitlist.createWaitlistNotification.useMutation();
  const { refetch: refetchWaitlist } = api.userWaitlist.getWaitlist.useQuery(
    { courseId },
    { enabled: !!user?.id }
  );

  // Min/max offer price — same feature as the unsolicited-offer flow: a fixed $10,000
  // ceiling, and a floor that's either the course's own configured minimum, or (when
  // that's unset) the cheapest actual green fee + cart fee for the course on this
  // date. Only relevant in NameYourPrice mode — waitlist mode has no price to name.
  const hasConfiguredMinimum = course?.nameYourPriceMinPerPlayer != null;
  // Null out in waitlist mode regardless of whatever selectedDate holds — encodes
  // "no min-price lookup needed here" directly into the value the query depends on,
  // rather than relying solely on `enabled` to skip the call.
  const selectedDateStr = !isWaitlistMode && selectedDate
    ? `${selectedDate.year}-${String(selectedDate.month).padStart(2, "0")}-${String(selectedDate.day).padStart(2, "0")}`
    : null;
  const { data: minimumOfferPriceData, isFetching: isFetchingMinimumOfferPrice } =
    api.nameYourPriceOffer.getMinimumOfferPrice.useQuery(
      {
        courseId,
        date: selectedDateStr ?? "",
        // Scope to the same window the offer will actually be submitted with
        // (startTime[0]/[1]) — otherwise this quotes a floor from tee times
        // outside the buyer's chosen window (e.g. a cheaper early-bird slot)
        // that they could never actually hit at this window.
        startTime: startTime[0],
        endTime: startTime[1],
      },
      { enabled: !isWaitlistMode && !hasConfiguredMinimum && !!selectedDateStr }
    );
  const rawMinimumPricePerGolfer = hasConfiguredMinimum
    ? (course?.nameYourPriceMinPerPlayer ?? 0) / 100
    : (minimumOfferPriceData?.minimumPerPlayer ?? 0) / 100;
  // The offer price input only accepts multiples of $10 (isPriceInvalid below) — a raw
  // minimum like $114 would otherwise be a floor the user can never actually enter,
  // rejecting even the exact amount just quoted to them. Round up to the next multiple
  // of 10 so the stated minimum is always enterable, without ever understating the real
  // floor (rounding up can't put it below the actual minimum).
  const minimumPricePerGolfer = rawMinimumPricePerGolfer > 0
    ? Math.ceil(rawMinimumPricePerGolfer / 10) * 10
    : 0;
  const maxAllowedPricePerGolfer = 10000;

  const isPriceInvalid = useMemo(() => {
    if (!pricePerPlayer) return false;
    const value = Number(pricePerPlayer);
    if (Number.isNaN(value)) return true;
    return (
      parseFloat(value.toFixed(2)) < parseFloat(minimumPricePerGolfer.toFixed(2)) ||
      parseFloat(value.toFixed(2)) > parseFloat(maxAllowedPricePerGolfer.toFixed(2)) ||
      value % 10 !== 0
    );
  }, [pricePerPlayer, minimumPricePerGolfer, maxAllowedPricePerGolfer]);

  // Strength is measured against the tee time's actual market value (the minimum
  // price for that date), NOT the absolute $10k input ceiling. Asymptotic curve:
  // percentage = (1 - value/price) — fills quickly just above value but keeps
  // visibly growing the higher you go and never quite reaches full, so there's
  // always an incentive to offer more. On a $100 tee time: $150 → 33% (moderate),
  // $200 → 50% (strong), $300 → 67%, $500 → 80%, $1000 → 90%.
  const offerStrength = useMemo(() => {
    const price = Number(pricePerPlayer);
    if (!price || minimumPricePerGolfer <= 0 || price < minimumPricePerGolfer) {
      return { level: "weak", percentage: 0, color: "bg-[#FF0000]", text: "Weak" };
    }
    const ratio = price / minimumPricePerGolfer;
    const percentage = Math.min((1 - 1 / ratio) * 100, 100);
    const strengthConfig = [
      { min: 0, max: 33.4, level: "weak", color: "bg-[#FF0000]", text: "Weak" },
      { min: 33.4, max: 50, level: "moderate", color: "bg-blue-500", text: "Moderate" },
      { min: 50, max: Infinity, level: "strong", color: "bg-green-500", text: "Strong" },
    ];
    const strength = strengthConfig.find((item) => percentage >= item.min && percentage < item.max);
    return {
      level: strength?.level ?? "weak",
      percentage,
      color: strength?.color ?? "bg-[#FF0000]",
      text: strength?.text ?? "Weak",
    };
  }, [pricePerPlayer, minimumPricePerGolfer]);

  // Live min/max feedback as the user types, separate from fieldErrors.price (which
  // only covers the "left it empty" case at submit time).
  const livePriceError =
    pricePerPlayer && isPriceInvalid
      ? Number(pricePerPlayer) < minimumPricePerGolfer
        ? `Minimum offer price is $${minimumPricePerGolfer.toFixed(2)} per player.`
        : Number(pricePerPlayer) > maxAllowedPricePerGolfer
          ? "Offer price is too high."
          : "Offer price must be in multiples of 10."
      : "";
  const priceErrorToShow = fieldErrors.price || livePriceError;

  // fieldErrors.price is only cleared by the price input's own onChange, but its
  // wording bakes in whatever minimumPricePerGolfer was at submit time — so after
  // switching date or time window (which changes the actual minimum, e.g. $114 on
  // one date vs $159 on another), it kept showing the OLD dollar amount and winning
  // out over the always-fresh livePriceError above, since priceErrorToShow prefers
  // fieldErrors.price when both are set. Clearing it here lets livePriceError (which
  // recomputes from the current minimumPricePerGolfer on every render) take over
  // immediately instead of waiting for the user to touch the price field again.
  useEffect(() => {
    setFieldErrors(f => ({ ...f, price: "" }));
  }, [selectedDateStr, startTime[0], startTime[1]]);

  // Build time options bounded by the course's own open/close timings, not the
  // full 24-hour day — so the slider (and tick labels below) only ever let a
  // buyer pick a window the course is actually open for. Offer mode snaps to
  // 1-hour blocks and waitlist mode to 30-min blocks; both are anchored at the
  // course's own open time rather than the top of the hour, since that open
  // time may not itself fall on an even hour (e.g. 6:30 AM).
  const formatTime = (value: number) => {
    const hour = Math.floor(value / 100);
    const minute = value % 100;
    const h = hour % 12 === 0 ? 12 : hour % 12;
    const period = hour < 12 ? "AM" : "PM";
    return `${h}:${minute === 0 ? "00" : minute} ${period}`;
  };
  // Convert to/from minutes-since-midnight so the stepping is correct even when
  // the course's open/close time isn't aligned to the hour (e.g. 6:30 AM).
  const hhmmToMinutes = (time: number) => Math.floor(time / 100) * 60 + (time % 100);
  const minutesToHhmm = (minutes: number) => Math.floor(minutes / 60) * 100 + (minutes % 60);

  const timeBlockMinutes = 30;
  const timeOptions: { displayTime: string; value: number }[] = [];
  const startMinutes = hhmmToMinutes(courseStartTimeNumber);
  const endMinutes = hhmmToMinutes(courseEndTimeNumber);
  for (let minutes = startMinutes; minutes <= endMinutes; minutes += timeBlockMinutes) {
    const value = minutesToHhmm(minutes);
    timeOptions.push({ displayTime: formatTime(value), value });
  }
  // Both modes now share the same 30-min block size, so this is a no-op today —
  // kept in case a mode-specific block size returns later, since without it a
  // time selected under one size could silently vanish from the grid under another.
  useEffect(() => {
    const snapToBlock = (time: number) => {
      const minutes = hhmmToMinutes(time);
      const steps = Math.round((minutes - startMinutes) / timeBlockMinutes);
      const snappedMinutes = Math.min(Math.max(startMinutes + steps * timeBlockMinutes, startMinutes), endMinutes);
      return minutesToHhmm(snappedMinutes);
    };
    setLocalStartTime(([s, e]) => [snapToBlock(s), snapToBlock(e)]);
    setStartTime(([s, e]) => [snapToBlock(s), snapToBlock(e)]);
  }, [timeBlockMinutes]);

  // Evenly spaced tick labels across whatever range the course is actually open,
  // instead of hardcoded 12 AM/6 AM/12 PM/6 PM/11 PM.
  const tickLabels = [0, 0.25, 0.5, 0.75, 1].map((fraction) => {
    const index = Math.round(fraction * (timeOptions.length - 1));
    return timeOptions[index]?.displayTime ?? "";
  });

  const handleSetStartTime = () => setStartTime(localStartTime);

  // Waitlist mode has no "how soon can this happen" cutoff — always at least
  // tomorrow. Make an Offer mode is bounded by OFFER_MIN_HOURS_BEFORE_TEE_TIME
  // instead (minHours) — a small value (e.g. 2) can still leave today selectable,
  // while a large one (>24) can push the earliest date past tomorrow. Mirrors the
  // same cutoff enforced in handleSendOffer's submit-time check below.
  const earliestOfferMoment = dayjs().tz(course?.timezoneISO ?? "America/Los_Angeles").add(minHours, "hour");
  const minimumDate = isWaitlistMode
    ? {
      year: dayjs().add(1, "day").year(),
      month: dayjs().add(1, "day").month() + 1,
      day: dayjs().add(1, "day").date(),
    }
    : {
      year: earliestOfferMoment.year(),
      month: earliestOfferMoment.month() + 1,
      day: earliestOfferMoment.date(),
    };
  // Match the same "how far out can this course be booked" rule used on the
  // main Find Times page ([course]/page.tsx's farthestDateOut), instead of a
  // flat one-year cap that ignores the course's own setting. Must anchor to
  // UTC like that page does — a plain dayjs() uses the viewer's local clock,
  // which can already be a calendar day ahead/behind UTC and drift this
  // calendar's max selectable date by one day versus Find Times.
  const furthestBookableDate = dayjs().utc().add(course?.furthestDayToBook ?? 0, "day");
  const maximumDate = {
    year: furthestBookableDate.year(),
    month: furthestBookableDate.month() + 1,
    day: furthestBookableDate.date(),
  };

  useEffect(() => {
    if (!selectedDate) { setDisplayDate(""); return; }
    setDisplayDate(
      dayjs(`${selectedDate.year}-${selectedDate.month}-${selectedDate.day}`).format("MMM DD, YYYY")
    );
  }, [selectedDate]);

  useEffect(() => {
    const datesToDisplay = selectedDates
      .slice()
      .sort(
        (a, b) =>
          new Date(a.year, a.month - 1, a.day).getTime() -
          new Date(b.year, b.month - 1, b.day).getTime()
      )
      .map((d) => dayjs(`${d.year}-${d.month}-${d.day}`).format("MMM DD"));
    setDisplayDates(datesToDisplay.join(", "));
  }, [selectedDates]);

  const isModified =
    (isWaitlistMode ? selectedDates.length > 0 : !!selectedDate) ||
    players !== "1" ||
    !!pricePerPlayer ||
    startTime[0] !== courseStartTimeNumber ||
    startTime[1] !== defaultWindowEnd;

  const resetForm = () => {
    setSelectedDate(null);
    setSelectedDates([]);
    setDisplayDate("");
    setDisplayDates("");
    setPlayers("1");
    setPricePerPlayer("");
    setLocalStartTime([courseStartTimeNumber, defaultWindowEnd]);
    setStartTime([courseStartTimeNumber, defaultWindowEnd]);
  };

  const handleReset = () => {
    resetForm();
    toast.info("Selections reset");
  };

  const handlePriceChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const val = e.target.value.replace(/[^0-9]/g, "");
    setPricePerPlayer(val);
  };

  const handleSendOffer = async () => {
    if (!isLoading && !user) {
      router.push(`/${courseId}/login`);
      return;
    }

    const errors = { date: "", price: "" };
    if (!selectedDate) {
      errors.date = "Please select a date to continue.";
    }
    if (!pricePerPlayer || Number(pricePerPlayer) <= 0) {
      errors.price = "Please enter a valid offer price to continue.";
    } else if (Number(pricePerPlayer) < minimumPricePerGolfer) {
      errors.price = `Minimum offer price is $${minimumPricePerGolfer.toFixed(2)} per player.`;
    } else if (Number(pricePerPlayer) > maxAllowedPricePerGolfer) {
      errors.price = `Maximum offer price is $${maxAllowedPricePerGolfer.toFixed(2)} per player.`;
    } else if (Number(pricePerPlayer) % 10 !== 0) {
      errors.price = "Offer price must be in multiples of 10.";
    }
    if (errors.date || errors.price) {
      setFieldErrors((f) => ({ ...f, ...errors }));
      // Scroll to the first empty field
      if (errors.date) {
        document.getElementById("nyp-pick-date")?.scrollIntoView({ behavior: "smooth", block: "center" });
      } else if (errors.price) {
        document.getElementById("nyp-offer-price")?.scrollIntoView({ behavior: "smooth", block: "center" });
      }
      return;
    }
    setFieldErrors({ date: "", price: "", phone: "" });

    if (!isPhoneVerified) {
      setFieldErrors((f) => ({ ...f, phone: "Please verify your phone number to continue." }));
      document.getElementById("nyp-verify-phone")?.scrollIntoView({ behavior: "smooth", block: "center" });
      return;
    }

    const dateStr = `${selectedDate!.year}-${String(selectedDate!.month).padStart(2, "0")}-${String(selectedDate!.day).padStart(2, "0")}`;

    // Mirrors the same cutoff enforced server-side in createOffer — checked here too
    // so the buyer gets immediate feedback instead of a round-trip failure, and so
    // the rule still holds even if minHours is ever configured above 24 (in which
    // case "tomorrow" alone wouldn't guarantee enough lead time).
    const timezoneISO = course?.timezoneISO ?? "America/Los_Angeles";
    const requestedStartTime = dayjs.tz(
      `${dateStr} ${String(Math.floor(startTime[0] / 100)).padStart(2, "0")}:${String(startTime[0] % 100).padStart(2, "0")}`,
      timezoneISO
    );
    if (requestedStartTime.isBefore(dayjs().tz(timezoneISO).add(minHours, "hour"))) {
      setFieldErrors((f) => ({
        ...f,
        date: `Offers must be made at least ${minHours} hour${minHours === 1 ? "" : "s"} before the requested tee time.`,
        price: "",
      }));
      document.getElementById("nyp-pick-date")?.scrollIntoView({ behavior: "smooth", block: "center" });
      return;
    }

    const duplicateCheck = await apiUtils.nameYourPriceOffer.checkDuplicateOffer.fetch({
      courseId,
      date: dateStr,
      startTime: startTime[0],
      endTime: startTime[1],
    });
    if (duplicateCheck?.isDuplicate) {
      setFieldErrors((f) => ({ ...f, date: "You already have an active offer for this date and time window." }));
      document.getElementById("nyp-pick-date")?.scrollIntoView({ behavior: "smooth", block: "center" });
      return;
    }

    const priceInCents = Math.round(Number(pricePerPlayer) * 100);

    googleAnalyticsEvent({
      action: "btn_nyp_send_offer",
      params: { selected_value: Number(pricePerPlayer), players: Number(players) },
    });

    onProceedToPayment({
      courseId,
      date: dateStr,
      startTime: startTime[0],
      endTime: startTime[1],
      players: Number(players),
      pricePerPlayer: priceInCents,
    });
  };

  const handleSetAlert = async () => {
    if (!isLoading && !user) {
      router.push(`/${courseId}/login`);
      return;
    }
    if (selectedDates.length === 0) {
      setFieldErrors((e) => ({ ...e, date: "Please select at least one date to continue." }));
      document.getElementById("nyp-pick-date")?.scrollIntoView({ behavior: "smooth", block: "center" });
      return;
    }
    setFieldErrors({ date: "", price: "", phone: "" });
    if (!isPhoneVerified) {
      setFieldErrors((f) => ({ ...f, phone: "Please verify your phone number to continue." }));
      document.getElementById("nyp-verify-phone")?.scrollIntoView({ behavior: "smooth", block: "center" });
      return;
    }

    await createNotifications(
      {
        courseId,
        startTime: startTime[0],
        endTime: startTime[1],
        dates: selectedDates.map((d) => `${d.year}-${d.month}-${d.day}`),
        playerCount: Number(players),
      },
      {
        onSuccess: (data) => {
          if (data.noChanges) {
            toast.info(data.message);
          } else {
            toast.success(
              <div>
                <p>{data.message}</p>
                <p
                  style={{ color: "var(--primary-color)" }}
                  className="text-[0.875rem] font-bold"
                >
                  If you don&apos;t see the notification emails please check your
                  Junk Mail or Spam folder. Remember to add
                  no-reply@golfdistrict.com to the safe senders list.
                </p>
              </div>,
              { progressStyle: { background: "var(--primary-color)" } }
            );
          }
          resetForm();
        },
      }
    );
    await refetchWaitlist();
  };

  const title = isWaitlistMode ? "Tee Time Waitlist" : "Make an Offer";
  const subtitle = isWaitlistMode
    ? "Get alerted when tee times are available"
    : "Broadcast a priced offer privately — ask other golfers if they're willing to sell their prepaid time.";

  const inner = (
    <>
      {/* Title */}
      <div className="px-4 md:px-8 mb-2 mt-2 md:mt-5">
        {/* Small branding eyebrow — only for the offer flow, not the plain
            waitlist mode, since "Price Point" names this specific feature. */}
        {!isWaitlistMode && (
          <div className="flex items-center justify-center gap-2 mb-1.5">
            <BlurImage
              alt="golf district logo"
              src={`https://${process.env.NEXT_PUBLIC_AWS_CLOUDFRONT_URL}/mobileheaderlogo.png`}
              width={24}
              height={24}
            />
            <span className="text-[0.9375rem] font-semibold uppercase tracking-wide text-primary-gray">
              Price Point
            </span>
          </div>
        )}
        <h1 className="md:text-center text-[1.25rem] capitalize text-secondary-black md:text-[2rem]">
          {title}
        </h1>
        <h2 className="md:text-center text-[0.875rem] text-primary-gray md:text-[1rem] mt-1">
          {subtitle}
        </h2>
      </div>

      <hr />

      <div className="grid gap-4 px-4 py-2 md:px-8 md:py-6">
        {/* Date picker — calendar shown directly inline, no click-to-open modal */}
        <div id="nyp-pick-date">
          <div className="flex items-center justify-between">
            <label className="text-[0.875rem] text-primary-gray">
              {isWaitlistMode ? "Pick date(s)" : "Pick date"}
            </label>
            {(isWaitlistMode ? displayDates : displayDate) && (
              <span className="text-[0.875rem] font-medium text-secondary-black text-right">
                {isWaitlistMode ? displayDates : displayDate}
              </span>
            )}
          </div>
          {fieldErrors.date && (
            <p className="text-red text-[0.75rem] mt-1">{fieldErrors.date}</p>
          )}
          {/* Both calendars stay permanently mounted — only visibility toggles
              with the waitlist checkbox. Swapping which <Calendar/> is mounted
              (and switching its `value` prop between a single Day and a Day[])
              made the vendor library recompute its active month/layout on
              every toggle, causing a visible jump. Two independent, always-
              mounted instances (each with a stable value type) never remount,
              so toggling the checkbox is now a pure CSS visibility flip. */}
          <div className={`mt-1 nyp-inline-calendar ${isWaitlistMode ? "" : "hidden"}`}>
            <SwipeableCalendar>
              <Calendar
                calendarClassName="!w-full !max-w-full unmask-time !text-[0.625rem]"
                colorPrimary="var(--primary-color)"
                value={selectedDates}
                onChange={(dates) => { setSelectedDates(dates); setFieldErrors(e => ({ ...e, date: "" })); }}
                minimumDate={minimumDate}
                maximumDate={maximumDate}
              />
            </SwipeableCalendar>
          </div>
          <div className={`mt-1 nyp-inline-calendar ${isWaitlistMode ? "hidden" : ""}`}>
            <SwipeableCalendar>
              <Calendar
                calendarClassName="!w-full !max-w-full unmask-time !text-[0.625rem]"
                colorPrimary="var(--primary-color)"
                value={selectedDate}
                onChange={(day) => { setSelectedDate(day!); setFieldErrors(e => ({ ...e, date: "" })); }}
                minimumDate={minimumDate}
                maximumDate={maximumDate}
              />
            </SwipeableCalendar>
          </div>
        </div>

        {/* Time window slider */}
        <div className="flex flex-col gap-2" id="nyp-time-range">
          <div className="flex items-center justify-between">
            <p className="text-[0.875rem] text-primary-gray">
              Time window
            </p>
            <span className="text-[0.875rem] font-medium text-secondary-black">
              {formatTime(localStartTime[0])} – {formatTime(localStartTime[1])}
            </span>
          </div>
          <Slider
            min={0}
            max={timeOptions.length - 1}
            step={1}
            value={[
              timeOptions.findIndex((i) => i.value === localStartTime[0]),
              timeOptions.findIndex((i) => i.value === localStartTime[1]),
            ]}
            onValueChange={(values) => {
              if (Array.isArray(values) && values.length === 2) {
                const s = timeOptions[values[0]!];
                const e = timeOptions[values[1]!];
                if (s && e) setLocalStartTime([s.value, e.value]);
              }
            }}
            onPointerUp={handleSetStartTime}
            aria-label="Select time window"
          />
          {/* Tick labels */}
          <div className="flex justify-between text-[0.625rem] text-primary-gray px-1">
            {tickLabels.map((label, i) => (
              <span key={`${label}-${i}`}>{label}</span>
            ))}
          </div>
        </div>

        {/* Number of players */}
        <div id="nyp-players">
          <p className="text-[0.875rem] text-primary-gray">
            Number of players
          </p>
          <div className="w-[16rem] mt-1">
            <ChoosePlayers
              className="py-2 !text-[0.625rem] md:!text-[0.875rem]"
              availableSlots={4}
              players={players}
              setPlayers={setPlayers}
              teeTimeId="-"
              playersOptions={["1", "2", "3", "4"]}
              numberOfPlayers={["1", "2", "3", "4"]}
              supportsGroupBooking={false}
            />
          </div>
        </div>

        {/* Offer price — when the NyP/waitlist choice exists it's always rendered in
            both modes, so the form's layout is literally identical whether the
            checkbox is checked or not: waitlist mode just dims and disables it in
            place (a price isn't needed for an alert) — nothing appears, disappears,
            or moves. When the course is waitlist-only (NyP flag off) there's no
            choice and no price concept at all, so the section isn't rendered. */}
        {!forceWaitlistOnly && (
          <div
            id="nyp-offer-price"
            className={`transition-opacity duration-300 ${isWaitlistMode ? "opacity-40 pointer-events-none select-none" : "opacity-100"}`}
            aria-disabled={isWaitlistMode}
          >
            <div className="flex items-center gap-2">
              <label htmlFor="nyp-offer-price-input" className="text-[0.875rem] text-primary-gray">
                Your offer price
              </label>
              <span className="text-[0.75rem] text-primary-gray">
                {isWaitlistMode ? "(not needed for alerts)" : "(per player)"}
              </span>
            </div>
            <div className="relative mt-1">
              <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 text-[0.875rem]">
                $
              </span>
              <input
                id="nyp-offer-price-input"
                className={`w-full rounded-lg bg-secondary-white pl-7 pr-4 py-3 text-[0.875rem] text-gray-500 outline-none ${priceErrorToShow && !isWaitlistMode ? "ring-1 ring-red" : ""}`}
                placeholder="0"
                value={pricePerPlayer}
                onChange={(e) => { handlePriceChange(e); setFieldErrors(f => ({ ...f, price: "" })); }}
                inputMode="numeric"
                disabled={isWaitlistMode}
              />
            </div>
            {/* Helper line — reserved height whether or not there's anything to say,
              so the spinner/error appearing never pushes the strength meter below. */}
            <p className={`min-h-[1.125rem] text-[0.75rem] mt-1 ${priceErrorToShow && !isWaitlistMode ? "text-red" : "text-gray-500"}`}>
              {!isFetchingMinimumOfferPrice && priceErrorToShow && !isWaitlistMode ? (
                priceErrorToShow
              ) : null}
            </p>

            {/* Offer strength — always mounted with a fixed footprint so it never
              shifts the layout; the label and bar fill simply update in place as
              the user types. Dims along with the rest of this section in waitlist mode. */}
            <div className="space-y-2 pt-2">
              <div className="flex items-center justify-between">
                <span className="text-xs font-medium text-gray-600">Offer Strength</span>
                <span
                  className={`text-xs font-semibold ${pricePerPlayer && !priceErrorToShow ? "" : "invisible"} ${offerStrength.level === "weak"
                    ? "text-red"
                    : offerStrength.level === "moderate"
                      ? "text-blue-600"
                      : "text-green-600"
                    }`}
                >
                  {offerStrength.text}
                </span>
              </div>
              <div className="relative h-2.5 bg-gradient-to-r from-gray-100 to-gray-200 rounded-full overflow-hidden shadow-inner">
                <div
                  className={`h-full transition-all duration-500 ease-out rounded-full ${offerStrength.color}`}
                  style={{ width: `${pricePerPlayer && !priceErrorToShow ? offerStrength.percentage : 0}%` }}
                />
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Phone verification — required before submitting so SMS updates can be sent */}
      <div id="nyp-verify-phone">
        <VerifyPhone onVerifiedChange={setIsPhoneVerified} />
        {fieldErrors.phone && (
          <p className="text-red text-[0.75rem] mt-1 mx-4 md:mx-8">{fieldErrors.phone}</p>
        )}
      </div>

      {/* Mode toggle — choose between naming a price or just joining the waitlist.
          Hidden entirely when this course doesn't support the waitlist at all. */}
      {!forceWaitlistOnly && course?.supportsWaitlist && (
        <div className="mx-4 md:mx-8 mt-3 rounded-xl border border-gray-200 p-3 flex items-start gap-3">
          <input
            type="checkbox"
            id="nyp-waitlist-mode"
            checked={isWaitlistMode}
            onChange={(e) => onWaitlistModeChange(e.target.checked)}
            className="mt-0.5 h-4 w-4 cursor-pointer accent-[var(--primary-color)]"
          />
          <label htmlFor="nyp-waitlist-mode" className="cursor-pointer">
            <p className="text-[0.875rem] font-medium text-secondary-black">
              I&apos;m not interested in making an offer to someone who owns the time. Put me on the waitlist if a time becomes available.
            </p>
          </label>
        </div>
      )}

      {/* Submit button */}
      <div className="flex justify-center items-center gap-3 mt-3 mb-4 px-4 md:px-8">
        <FilledButton
          onClick={isWaitlistMode ? handleSetAlert : handleSendOffer}
          disabled={!isPhoneVerified || (isWaitlistMode && isCreatingNotifications)}
          className="flex items-center justify-center gap-1 w-full max-w-[14rem] disabled:opacity-50 transition-opacity duration-300"
          id="nyp-send-offer"
        >
          {isWaitlistMode ? (forceWaitlistOnly ? "Get Alerted" : "Set Alert") : "Continue to Payment"}
        </FilledButton>
        <OutlineButton
          onClick={handleReset}
          disabled={!isModified}
          className={`flex items-center justify-center gap-1 w-full max-w-[14rem] transition-opacity duration-300 ${!isModified ? "opacity-50 cursor-not-allowed" : ""}`}
        >
          <Refresh color="var(--primary-color)" className="w-3 md:w-4" />
          Reset
        </OutlineButton>
      </div>

      {/* Disclaimer — offer mode already shows this messaging in the subtitle above, so only waitlist mode needs it here. */}
      {isWaitlistMode && (
        <div className="flex justify-center items-center text-justify mt-2 italic text-primary-gray text-[0.75rem] md:text-[0.875rem] px-4 py-2 md:px-8">
          <p>
            {forceWaitlistOnly
              ? "Bookings are paid in advance and non-refundable. If plans change simply list your time for sale, and easily cash out."
              : "You will be alerted instantly when a matching tee time opens. Bookings are non-refundable once confirmed."}
          </p>
        </div>
      )}
    </>
  );

  if (hideWrapper) return inner;

  return (
    <div className="flex flex-col gap-1 bg-white py-2 rounded-xl md:py-6 shadow">
      {inner}
    </div>
  );
}
